import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Document, Page } from "react-pdf";
import { isMobile } from "../../utils/miscutils";

const FilePreviewDialog = ({
  previewModal,
  onClose,
  onDocumentLoadSuccess,
  numPages,
  pageNumber,
  setPageNumber,
}) => {
  const handleClose = () => {
    onClose();
  };

  const handleDownload = () => {
    if (previewModal.url && previewModal.fileObject) {
      const link = document.createElement("a");
      link.href = previewModal.url;
      link.download = previewModal.name || (previewModal.isPdf ? "document.pdf" : "image.jpg");
      link.click();
    }
  };

  return (
    <Dialog
      open={previewModal.open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: { xs: "16px", sm: "32px" },
          maxHeight: {
            xs: "calc(100% - 32px)",
            sm: "90vh",
          },
          width: {
            xs: "calc(100% - 32px)",
            sm: "100%",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: {
            xs: "12px 16px",
            sm: "16px 24px",
          },
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "18px" },
            fontWeight: 600,
            color: "#252D38",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "70%",
          }}
        >
          {previewModal.name}
        </Typography>
        <Box
          onClick={handleClose}
          sx={{
            cursor: "pointer",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            color: "#252D38",
          }}
        >
          <CloseIcon />
        </Box>
      </Box>

      <Box
        sx={{
          padding: { xs: "16px", sm: "24px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          minHeight: { xs: "200px", sm: "300px" },
          maxHeight: { xs: "60vh", sm: "70vh" },
          overflow: "auto",
        }}
      >
        {previewModal.isPdf ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "16px",
                overflow: "auto",
              }}
            >
              <Document
                file={previewModal.url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <Box
                    sx={{
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography
                      sx={{
                        mt: 2,
                        color: "#47566A",
                      }}
                    >
                      Loading PDF...
                    </Typography>
                  </Box>
                }
                error={
                  <Box
                    sx={{
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ color: "#FC1721" }}>
                      Failed to load PDF
                    </Typography>
                  </Box>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={Math.min(window.innerWidth - 100, 600)}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>

              {numPages && numPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 2,
                    padding: "8px 16px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={pageNumber <= 1}
                    onClick={() => setPageNumber(pageNumber - 1)}
                    sx={{
                      minWidth: "40px",
                      padding: "4px 8px",
                      borderColor: "#0066DC",
                      color: "#0066DC",
                    }}
                  >
                    Prev
                  </Button>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#47566A",
                    }}
                  >
                    Page {pageNumber} of {numPages}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={pageNumber >= numPages}
                    onClick={() => setPageNumber(pageNumber + 1)}
                    sx={{
                      minWidth: "40px",
                      padding: "4px 8px",
                      borderColor: "#0066DC",
                      color: "#0066DC",
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>

            {!isMobile(window.innerWidth) && (<Button
              variant="outlined"
              onClick={handleDownload}
              sx={{
                borderColor: "#0066DC",
                color: "#0066DC",
                borderRadius: "8px",
                padding: "8px 20px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                width: "100%",
                "&:hover": {
                  borderColor: "#0052B4",
                  backgroundColor: "#F0F7FF",
                },
              }}
            >
              Download PDF
            </Button>
            )}
          </Box>
        ) : previewModal.url ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxHeight: { xs: "50vh", sm: "60vh" },
                overflow: "auto",
                display: "flex",
                justifyContent: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
                padding: { xs: "8px", sm: "16px" },
                border: "1px solid #E5E7EB",
              }}
            >
              <img
                src={previewModal.url}
                alt={previewModal.name}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  objectFit: "contain",
                }}
              />
            </Box>
            {!isMobile(window.innerWidth) && (<Button
              variant="outlined"
              onClick={handleDownload}
              sx={{
                borderColor: "#0066DC",
                color: "#0066DC",
                borderRadius: "8px",
                padding: "8px 20px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  borderColor: "#0052B4",
                  backgroundColor: "#F0F7FF",
                },
              }}
            >
              Download Image
            </Button>
            )}
          </Box>
        ) : (
          <Typography
            sx={{
              color: "#47566A",
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            No preview available
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default FilePreviewDialog;